import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { Navigate, Outlet } from 'react-router';
import { Permission } from '../../@types';
import usePermissions from '../../hooks/usePermissions';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import RequireTenantGuard from '../../RequireTenantGuard';
import { USER_ROLES } from '../../constants/keycloak';
import { useMarketplace } from '../../hooks/useMarketplace';
import { CasinoConfigFlag, MarketplaceFeatureFlag, MarketplaceSystemFlag } from '../../@types/api';

type Props = {
  permission: Permission;
  requireTenant?: boolean;
  featureFlag?: string;
  casinoConfigFlag?: CasinoConfigFlag;
  marketplaceFeatureFlag?: MarketplaceFeatureFlag;
  marketplaceSystemFlag?: MarketplaceSystemFlag;
  standaloneCasinoSystemFlag?: MarketplaceSystemFlag;
  requireSportsbook?: boolean;
  requireCasino?: boolean;
};

const checkFlag = (flagName?: PropertyKey, flags?: object): boolean => {
  if (!flagName) return true;

  return (flags as Record<PropertyKey, unknown> | undefined)?.[flagName] === true;
};

const ProtectedPage = ({
  permission,
  requireTenant,
  featureFlag = '',
  casinoConfigFlag,
  marketplaceFeatureFlag,
  marketplaceSystemFlag,
  standaloneCasinoSystemFlag,
  requireSportsbook = false,
  requireCasino = false,
}: Props) => {
  const { userId } = useAuth();
  const { hasPermission, role } = usePermissions();
  const { isStandaloneCasino, config, isLoading: isMarketplaceLoading } = useMarketplace();
  const featureIsOnValue = useFeatureIsOn(featureFlag);

  const growthbookFeatureIsOn = featureFlag ? featureIsOnValue : true;

  const marketplaceFeatureIsOn = checkFlag(marketplaceFeatureFlag, config?.marketplaceConfig?.features);
  // Direct Casino routes use the same default-deny configuration check as their tabs.
  const casinoConfigFeatureIsOn = checkFlag(casinoConfigFlag, config?.casinoConfig);
  const marketplaceSystemIsOn = checkFlag(marketplaceSystemFlag, config?.marketplaceConfig?.systems);
  const standaloneCasinoSystemIsOn = isStandaloneCasino
    ? checkFlag(standaloneCasinoSystemFlag, config?.marketplaceConfig?.systems)
    : true;
  const casinoProductIsOn = requireCasino ? config?.marketplaceConfig?.products?.casino === true : true;

  const featureIsOn =
    growthbookFeatureIsOn &&
    marketplaceFeatureIsOn &&
    casinoConfigFeatureIsOn &&
    marketplaceSystemIsOn &&
    standaloneCasinoSystemIsOn &&
    casinoProductIsOn;

  const requiresMarketplaceConfig = !!(
    marketplaceFeatureFlag ||
    casinoConfigFlag ||
    marketplaceSystemFlag ||
    standaloneCasinoSystemFlag ||
    requireCasino
  );

  // Wait for MP config to load before checking flags
  if (requiresMarketplaceConfig && !config && isMarketplaceLoading) {
    return null;
  }

  if (requireSportsbook && isStandaloneCasino) {
    return <Navigate to='/' replace />;
  }

  if (!userId || !hasPermission(permission) || !featureIsOn) {
    return <Navigate to='/' replace />;
  }

  if (role === USER_ROLES.SUPER_ADMIN && requireTenant) {
    return (
      <RequireTenantGuard>
        <Outlet />
      </RequireTenantGuard>
    );
  }

  return <Outlet />;
};

export default ProtectedPage;
